import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteDocument,
  findByFileName,
  isSupportedFile,
  listDocuments,
  listProperties,
  listTenants,
  reindexDocument,
  replaceDocument,
  uploadDocument,
  type DocumentCollection,
  type LibraryDocument,
} from './api';

export const documentKeys = {
  all: ['documents'] as const,
  list: (collection: DocumentCollection, q: string, propertyKeys: string[]) =>
    [...documentKeys.all, 'list', collection, q, propertyKeys] as const,
  properties: ['properties'] as const,
  tenants: (propertyKey: string) => ['properties', propertyKey, 'tenants'] as const,
};

/** propertyKeys empty = every property. */
export function useDocuments(collection: DocumentCollection, q: string, propertyKeys: string[] = []) {
  return useQuery({
    queryKey: documentKeys.list(collection, q, propertyKeys),
    queryFn: ({ signal }) =>
      listDocuments(
        { collection, q: q || undefined, propertyKeys: propertyKeys.length ? propertyKeys : undefined },
        signal,
      ),
  });
}

export function useProperties() {
  return useQuery({ queryKey: documentKeys.properties, queryFn: ({ signal }) => listProperties(signal) });
}

export function useTenants(propertyKey: string) {
  return useQuery({
    queryKey: documentKeys.tenants(propertyKey),
    queryFn: ({ signal }) => listTenants(propertyKey, signal),
    enabled: !!propertyKey,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: documentKeys.all });
}

export interface UploadVars {
  file: File;
  name?: string;
  collection: DocumentCollection;
  propertyKey: string;
  partyId?: string;
  onStep?: (step: 'uploading' | 'indexing') => void;
}

export function useUploadDocument() {
  const done = useInvalidate();
  return useMutation({
    mutationFn: (v: UploadVars) =>
      uploadDocument(
        v.file,
        { name: v.name, collection: v.collection, propertyKey: v.propertyKey, partyId: v.partyId },
        v.onStep,
      ),
    onSettled: done,
  });
}

export function useReplaceDocument() {
  const done = useInvalidate();
  return useMutation({
    mutationFn: (v: { id: string; file: File }) => replaceDocument(v.id, v.file),
    onSettled: done,
  });
}

export function useReindexDocument() {
  const done = useInvalidate();
  return useMutation({ mutationFn: (id: string) => reindexDocument(id), onSettled: done });
}

export function useDeleteDocument() {
  const done = useInvalidate();
  return useMutation({ mutationFn: (id: string) => deleteDocument(id), onSettled: done });
}

export type BatchOutcome = 'waiting' | 'uploading' | 'created' | 'replaced' | 'failed' | 'skipped';

export interface BatchItem {
  file: File;
  outcome: BatchOutcome;
  detail?: string;
}

const MAX_BYTES = 100 * 1024 * 1024; // sreg-api's limit

/**
 * AppFolio exports, several at once, for one property: a file whose name is
 * already in the property's reports replaces it (a new revision); any other
 * is created. One at a time, so each finishes indexing before the next.
 */
export function useUploadReports() {
  const done = useInvalidate();
  return useMutation({
    mutationFn: async (v: { files: File[]; propertyKey: string; onItem: (i: number, item: BatchItem) => void }) => {
      for (const [i, file] of v.files.entries()) {
        const report = (outcome: BatchOutcome, detail?: string) => v.onItem(i, { file, outcome, detail });
        if (!isSupportedFile(file.name)) {
          report('skipped', 'not a file type Simon can search');
          continue;
        }
        if (file.size > MAX_BYTES) {
          report('skipped', 'over 100 MB');
          continue;
        }
        report('uploading');
        try {
          const same = (await findByFileName('lease_reports', file.name)).find((d) => d.propertyKey === v.propertyKey);
          const doc: LibraryDocument = same
            ? await replaceDocument(same.id, file)
            : await uploadDocument(file, {
                name: file.name.replace(/\.[^.]+$/, ''),
                collection: 'lease_reports',
                propertyKey: v.propertyKey,
              });
          if (doc.status === 'indexed') report(same ? 'replaced' : 'created', `revision ${doc.revision}`);
          else report('failed', doc.indexError ?? `stored, but ${doc.status}`);
        } catch (e) {
          report('failed', e instanceof Error ? e.message : String(e));
        }
      }
    },
    onSettled: done,
  });
}
